/**
 * Shared cache TTL configuration (importable by both server and client).
 *
 * RELATIONSHIP BETWEEN SERVER AND CLIENT TTLs:
 * - Server (KV cache in cache_Service) stores responses for SERVER_TTL_SEC seconds.
 * - Client (useDataPersistence) refetches when data is older than CLIENT_TTL_MS milliseconds.
 * - INVARIANT: For every data type that has server cache, SERVER_TTL_SEC >= CLIENT_TTL_MS/1000.
 *   Otherwise the client would refetch more often than the server can serve fresh data, causing
 *   unnecessary DB load and cache hits that are still stale from the client’s perspective.
 * - When adding a new cached entity: define both TTLs here and keep client TTL <= server TTL.
 */

/** @typedef {'notes'|'lessons'|'quizzes'|'classrooms'|'userData'|'payments'} ClientDataType */

/**
 * Server-side KV cache TTLs (seconds). Used by server/services/cache_Service.js.
 * Keys match the cache helper usage (e.g. NOTES_LIST, CLASSROOM_LIST).
 */
export const SERVER_TTL_SEC = Object.freeze({
  NOTES_LIST: 300,
  NOTE_SINGLE: 600,
  TAGS: 1800,
  CLASSROOM_LIST: 300,
  CLASSROOM_SINGLE: 600,
  LESSON_LIST_CLASSROOM: 300,
  LESSON_SINGLE: 600,
  QUIZ_LIST_CLASSROOM: 300,
  QUIZ_SINGLE: 600
});

/**
 * Client-side refetch intervals (milliseconds). Used by src/hooks/useDataPersistence.ts.
 * Keys are the dataType strings passed to shouldRefresh().
 * For types that have server cache (notes, lessons, quizzes, classrooms), value must be
 * <= SERVER_TTL_SEC corresponding value * 1000.
 * For client-only types (userData, payments, messaging) there is no server cache; these are the only TTLs.
 */
export const CLIENT_TTL_MS = Object.freeze({
  notes: 300 * 1000,        // 5 min, matches SERVER_TTL_SEC.NOTES_LIST
  lessons: 300 * 1000,      // 5 min, matches SERVER_TTL_SEC.LESSON_LIST_CLASSROOM
  quizzes: 300 * 1000,      // 5 min, matches SERVER_TTL_SEC.QUIZ_LIST_CLASSROOM
  classrooms: 300 * 1000,   // 5 min, matches SERVER_TTL_SEC.CLASSROOM_LIST
  userData: 2 * 60 * 1000,  // 2 min, no server cache
  payments: 5 * 60 * 1000,  // 5 min, no server cache
  messaging: 30 * 1000      // 30 s, no server cache
});

/**
 * Backward-compatible CACHE_TTL for server: same values as SERVER_TTL_SEC (seconds).
 * Prefer SERVER_TTL_SEC in new code.
 */
export const CACHE_TTL = Object.freeze({ ...SERVER_TTL_SEC });
